import React, { useState } from 'react';
import { useHistory } from "react-router-dom";
import { useFormik } from 'formik';
import * as yup from 'yup';

/* Styles */
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import onboardingStyles from 'styles/onboarding.module.scss';
import cn from 'classnames';

/* i18n */
import { useIntl } from 'react-intl';

/* API */
import { getIndustrySuggestions, getJobSuggestions, postProfile } from 'lib/API';

/* Redux */
import { useDispatch, useSelector } from 'react-redux';
import { setExperienceForm } from 'app/slices/experienceSlice';
import { setCurrentSoc } from 'app/slices/jobsSlice';

/* Material UI and other UI Dependencies */
import Typography from "@material-ui/core/Typography";
import OnboardingSteps from 'components/OnboardingSteps';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText
} from "@material-ui/core";
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import ExperienceInput from 'components/inputs/ExperienceInput';
import ZipcodeInput from 'components/inputs/ZipcodeInput';
import Autocomplete from '@material-ui/lab/Autocomplete';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

/* Analytics */
import { trackEvent } from 'lib/analytics';

/* Misc */
import { debounce } from 'lib/performance';

export default function OnboardingContact() {
  const [submitting, setSubmitting] = useState(false);
  const [contactError, setContactError] = useState(false);
  const [industryOpen, setIndustryOpen] = React.useState(false);
  const [jobOpen, setJobOpen] = React.useState(false);
  const [industrySuggestions, setIndustrySuggestions] = useState([]);
  const [jobSuggestions, setJobSuggestions] = useState([]);
  const [industryLoading, setIndustryLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);

  const dispatch = useDispatch();
  const experienceFormInitialValues = useSelector((state) => state.experience.form);
  const industryInitialValue = useSelector((state) => state.experience.industry.naics2_code);

  const industryChange = debounce(async function({ target: { value }}) {
    value = value.trim();
    if (value === '') {
      return setIndustryLoading(false);
    }
    if (value.length > 0) {
      setIndustryLoading(true);
      const suggestions = await getIndustrySuggestions(value, intl.locale, industryInitialValue);
      if (suggestions !== false) {
        setIndustrySuggestions(suggestions);
      }
      setIndustryLoading(false);
    }
  }, 380);

  const jobChange = debounce(async ({ target: { value }}) => {
    value = value.trim();
    if (value === '') {
      return setJobLoading(false);
    }
    if (value.length > 0) {
      setJobLoading(true);
      const suggestions = await getJobSuggestions(value, intl.locale);
      if (suggestions !== false) {
        setJobSuggestions(suggestions);
      }
      setJobLoading(false);
    }
  }, 380);

  const highlightLabel = (option, search) => {
    if (search !== undefined && search.inputValue !== undefined) {
        const matches = match(option.title.trim(), search.inputValue);
        const parts = parse(option.title.trim(), matches);
        return <span>{parts.map((part, index) => part.highlight ? <b>{part.text}</b> : part.text)}</span>;
    } else {
        return <span>{option.title}</span>;
    }
  };

  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const history = useHistory();
  
  const educationOptions = [
    {value: 11, title: intl.formatMessage({id:'forms.onboarding.education.noDiploma', description:'string'})},
    {value: 12, title: intl.formatMessage({id:'forms.onboarding.education.diploma', description:'string'})},
    {value: 13, title: intl.formatMessage({id:'forms.onboarding.education.someCollege', description:'string'})},
    {value: 14, title: intl.formatMessage({id:'forms.onboarding.education.assocDegree', description:'string'})},
    {value: 16, title: intl.formatMessage({id:'forms.onboarding.education.bachDegree', description:'string'})},
    {value: 17, title: intl.formatMessage({id:'forms.onboarding.education.mastersDegree', description:'string'})},
    {value: 20, title: intl.formatMessage({id:'forms.onboarding.education.advancedDegree', description:'string'})},
  ];

  /**
   * This function always returns all of the suggestions so the filtering
   * can be made on the server
   */
  const filterOptions = (options) => options;
  
  const validationSchema = yup.object({
    soc_code: yup
        .number( intl.formatMessage({id:'forms.onboarding.occupationValidation', description:'string'}))
        .required( intl.formatMessage({id:'forms.onboarding.occupationRequired', description:'string'})),
    naics_code: yup
        .number( intl.formatMessage({id:'forms.onboarding.industryValidation', description:'string'}))
        .required( intl.formatMessage({id:'forms.onboarding.industryRequired', description:'string'})),
    years_experience: yup
        .number( intl.formatMessage({id:'forms.onboarding.yearsExperienceValidation', description:'string'}))
        .max(100, intl.formatMessage({id:'forms.onboarding.yearsExperienceMax', description:'string'}) )
        .required( intl.formatMessage({id:'forms.onboarding.yearsExperienceRequired', description:'string'})),
    education_level: yup
        .string( intl.formatMessage({id:'forms.onboarding.educationValidation', description:'string'}))
        .required( intl.formatMessage({id:'forms.onboarding.educationRequired', description:'string'})),
    zipcode: yup
        .string( intl.formatMessage({id:'forms.onboarding.zipcode', description:'string'}))
        .matches(/^[0-9]+$/, intl.formatMessage({id:'forms.onboarding.onlyDigits', description:'string'}))
        .min(5, intl.formatMessage({id:'forms.onboarding.exactlyXDigits', description:'string', }, {amount:5}))
        .max(5, intl.formatMessage({id:'forms.onboarding.exactlyXDigits', description:'string'}, {amount:5}))
  });

  let initialValues = {
    soc_code: '',
    naics_code: '',
    years_experience: '',
    education_level: '',
    zipcode: '',
    ...experienceFormInitialValues,
    action: 'experience'
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
      onSubmit: async (values) => {
        trackEvent('ONBOARDING_RESUME_MANUALENTRY');
        setSubmitting(true);
        dispatch(setExperienceForm(values));
        dispatch(setCurrentSoc(values.soc_code));
        postProfile(values).then((response) => {
          setSubmitting(false);
          history.push(`${urlPrefix}/onboarding/goals`);
        })
        setContactError('');
    },
  });

  return (
    <>
      <div className={styles.loginFlow__form__onboarding}>
        <Box px={3}>
          <Typography align="center" variant="h1"  gutterBottom>
            {intl.formatMessage({id:'onboarding.resume.title',description:'string'})}
          </Typography>
        </Box>
        <OnboardingSteps activeStep={1} />
      </div>
      <hr className={layoutStyles.separatorBold} />
      <div className={styles.onboardingExperience__cardsContainer}>
        <Typography align="center" variant="h5"  gutterBottom>
          {intl.formatMessage({id:'onboarding.experience.enterJobExperience',description:'string'})}
        </Typography>
        <Box mb={3} />
        <form onSubmit={formik.handleSubmit} className={cn(styles.loginFlow__form, styles.signInFormNoStripe, onboardingStyles.formContainer)}>
          <Autocomplete
            id="autocomplete-industry"
            filterOptions={filterOptions}
            defaultValue={{code: formik.values.naics_code, title: formik.values.naics_title}}
            open={industryOpen}
            onOpen={() => setIndustryOpen(true)}
            onClose={() => setIndustryOpen(false)}
            getOptionSelected={(option, value) => option.title === value.title}
            renderOption={highlightLabel}
            getOptionLabel={(option) => option.title}
            groupBy={(option) => option.group}
            options={industrySuggestions}
            loading={industryLoading}
            onChange={(event, newValue) => {
                if (!newValue) {
                    formik.setFieldValue('naics_code', '');
                    formik.setFieldValue('naics_title', '');
                    formik.setFieldValue('naics_query', '');
                } else {
                    formik.setFieldValue('naics_code', newValue.code);
                    formik.setFieldValue('naics_title', newValue.title);
                }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({id:'forms.onboarding.industry'})}
                placeholder={intl.formatMessage({id:'forms.onboarding.industryPlaceholder'})}
                variant="filled"
                name="naics_code"
                error={formik.touched.naics_code && Boolean(formik.errors.naics_code)}
                helperText={formik.touched.naics_code && formik.errors.naics_code}
                fullWidth
                FormHelperTextProps={{
                  className: styles.helperText
                }}
                onChange={(event) => {
                  industryChange(event);
                  formik.setFieldValue('naics_query', event.target.value);
                }}
                onBlur={formik.handleBlur}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {industryLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            id="autocomplete-job"
            filterOptions={filterOptions}
            defaultValue={{code: formik.values.soc_code, title: formik.values.soc_title}}
            open={jobOpen}
            onOpen={() => setJobOpen(true)}
            onClose={() => setJobOpen(false)}
            getOptionSelected={(option, value) => option.title === value.title}
            renderOption={highlightLabel}
            getOptionLabel={(option) => option.title}
            groupBy={(option) => option.group}
            options={jobSuggestions}
            loading={jobLoading}
            onChange={(event, newValue) => {
                if (!newValue) {
                    formik.setFieldValue('soc_code', '');
                    formik.setFieldValue('soc_title', '');
                    formik.setFieldValue('soc_query', '');
                } else {
                    formik.setFieldValue('soc_code', newValue.code);
                    formik.setFieldValue('soc_title', newValue.title);
                }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({id:'forms.onboarding.jobTitle', description:'string'})}
                placeholder={intl.formatMessage({id:'forms.onboarding.occupationPlaceholder', description:'string'})}
                variant="filled"
                name="soc_code"
                error={formik.touched.soc_code && Boolean(formik.errors.soc_code)}
                helperText={formik.touched.soc_code && formik.errors.soc_code}
                fullWidth
                FormHelperTextProps={{
                  className: styles.helperText
                }}
                onChange={(event) => {
                  jobChange(event);
                  formik.setFieldValue('soc_query', event.target.value);
                }}
                onBlur={formik.handleBlur}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {jobLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          <TextField
            FormHelperTextProps={{
              className: styles.helperText
            }}
            error={formik.touched.years_experience && Boolean(formik.errors.years_experience)}
            name="years_experience"
            type="text"
            variant="filled"
            fullWidth
            helperText={formik.touched.years_experience && formik.errors.years_experience}
            autoComplete="off"
            label={intl.formatMessage({id:'forms.onboarding.yearsOfExperience'})}
            value={formik.values.years_experience}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            InputProps={{
              inputComponent: ExperienceInput
            }}
            inputProps={{
              title: intl.formatMessage({id: 'forms.onboarding.yearsOfExperience'})
            }}
          />
          <FormControl variant="filled" error={formik.touched.education_level && formik.errors.education_level}>
            <InputLabel id="education-level-label">
              {intl.formatMessage({id:'forms.onboarding.educationLevel'})}
            </InputLabel>
            <Select
              name="education_level"
              labelId="education-level-label"
              id="education-level"
              value={formik.values.education_level}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {educationOptions.map(eo => (
                <MenuItem key={eo.value} value={eo.value}>{eo.title}</MenuItem>
              ))}
            </Select>
            {formik.touched.education_level && formik.errors.education_level && (
              <FormHelperText>{formik.errors.education_level}</FormHelperText>
            )}
          </FormControl>
          <TextField
            FormHelperTextProps={{
              className: styles.helperText
            }}
            error={formik.touched.zipcode && Boolean(formik.errors.zipcode)}
            name="zipcode"
            type="text"
            variant="filled"
            fullWidth
            helperText={formik.touched.zipcode && formik.errors.zipcode}
            autoComplete="off"
            label={intl.formatMessage({id:'forms.onboarding.zipcode'})}
            value={formik.values.zipcode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            InputProps={{
              inputComponent: ZipcodeInput
            }}
            inputProps={{
              title: intl.formatMessage({id: 'forms.onboarding.zipcode'})
            }}
          />
          {contactError.length > 0 && (
            <div className={styles.errorMessage__container}>
              <label className={styles.errorMessage}>{contactError}</label>
            </div>
          )}
          <Box mb={2} />
          <div className={styles.submitBtn__container}>
            <Button
              color="secondary"
              disabled={submitting}
              size="large"
              type="submit"
              variant="contained"
            >
              {
                submitting ? (
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">
                      {intl.formatMessage({id:'forms.signIn.loading', description:'string'})}
                    </span>
                  </div>
                )
                : (intl.formatMessage({id:'forms.onboarding.next', description:'string'}))
              }
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}